"use client";

import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import CreateJobDialog from "@/modules/jobs/components/CreateJobDialog";
import AlreadyAppliedDialog from "@/shared/components/dialogs/AlreadyAppliedDialog";

import {
  fetchJobById,
  deleteJob,
} from "@/modules/jobs/store/jobThunk";

import type {
  Job,
  JobFilters,
} from "@/modules/jobs/types/job";

import { useDashboardJobs } from "@/modules/jobs/hooks/useDashboardJobs";

import { Card, CardContent } from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Badge } from "@/shared/components/ui/badge";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";

import {
  Plus,
  Pencil,
  Trash2,
  Search,
  MapPin,
  GraduationCap,
  Users,
  Eye,
  Loader2,
} from "lucide-react";


// =========================================================
// SALARY FORMATTING
// =========================================================

const getCurrencySymbol = (
  currency?: string | null
) => {
  switch (String(currency || "INR").trim().toUpperCase()) {
    case "INR":
      return "₹";
    case "USD":
      return "$";
    case "EUR":
      return "€";
    case "GBP":
      return "£";
    case "AED":
      return "د.إ";
    case "CAD":
      return "C$";
    case "AUD":
      return "A$";
    case "SGD":
      return "S$";
    default:
      return String(currency || "INR").trim();
  }
};

const normalizeSalaryToLPA = (
  value: number | string | null | undefined
) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return null;
  }

  if (numericValue >= 1 && numericValue < 1000) {
    return numericValue;
  }

  if (numericValue >= 1000) {
    return numericValue / 100000;
  }

  if (numericValue > 0 && numericValue < 1) {
    return numericValue * 100000;
  }

  return numericValue;
};

const formatSalaryRange = (
  minimumSalary: number | string | null | undefined,
  maximumSalary: number | string | null | undefined,
  currency?: string | null
) => {
  const minimum = normalizeSalaryToLPA(minimumSalary);
  const maximum = normalizeSalaryToLPA(maximumSalary);

  if (minimum === null && maximum === null) {
    return "—";
  }

  const symbol = getCurrencySymbol(currency);

  const formatValue = (value: number | null) => {
    if (value === null) {
      return "—";
    }

    return Number.isInteger(value)
      ? String(value)
      : value.toFixed(2).replace(/0+$/, "").replace(/\.$/, "");
  };

  return `${symbol}${formatValue(minimum)} - ${symbol}${formatValue(maximum)} LPA`;
};

export default function JobsAdminPage() {
  const dispatch = useDispatch<any>();

  /* =========================================================
     LOCAL UI STATE
  ========================================================= */

  const [searchQuery, setSearchQuery] = useState("");

  const [debouncedSearch, setDebouncedSearch] =
    useState("");

  const [filterStatus, setFilterStatus] =
    useState("all");

  const [showDialog, setShowDialog] =
    useState(false);

  const [editingJob, setEditingJob] =
    useState<Job | null>(null);

  const [viewingJob, setViewingJob] =
    useState<Job | null>(null);

  const [loadingEditJobId, setLoadingEditJobId] =
    useState<string | null>(null);

  const [loadingViewJobId, setLoadingViewJobId] =
    useState<string | null>(null);

  const [loadingDeleteJobId, setLoadingDeleteJobId] =
    useState<string | null>(null);

  const [showDeleteSuccessPopup, setShowDeleteSuccessPopup] =
    useState(false);

  const [deletedJobTitle, setDeletedJobTitle] = useState("");
  const [showDeletePopup, setShowDeletePopup] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<{
    id: string;
    title: string;
  } | null>(null);


  /* =========================================================
     SEARCH DEBOUNCE
  ========================================================= */

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, 400);

    return () => {
      window.clearTimeout(timer);
    };
  }, [searchQuery]);

  /* =========================================================
     API FILTERS
  ========================================================= */

  const jobFilters = useMemo<JobFilters>(() => {
    const filters: JobFilters = {
      search: debouncedSearch,
      page: 1,
    };

    if (filterStatus === "active") {
      filters.isActive = true;
    }

    if (filterStatus === "inactive") {
      filters.isActive = false;
    }

    if (filterStatus === "urgent") {
      filters.priority = "HIGH";
    }

    return filters;
  }, [debouncedSearch, filterStatus]);

  /* =========================================================
     FETCH ADMIN JOBS
  ========================================================= */

  const {
    jobs,
    loading,
    error,
    pagination,
  } = useDashboardJobs(jobFilters);

  /* =========================================================
     EDIT JOB
  ========================================================= */

  const handleEditJob = async (jobId: string) => {
    if (loadingEditJobId) {
      return;
    }

    try {
      setLoadingEditJobId(jobId);

      const fullJob = await dispatch(
        fetchJobById(jobId)
      ).unwrap();

      setEditingJob(fullJob);

      setShowDialog(true);
    } catch (error) {
      console.error(
        "Failed to load complete job details:",
        error
      );
    } finally {
      setLoadingEditJobId(null);
    }
  };

  /* =========================================================
     VIEW JOB
  ========================================================= */

  const handleViewJob = async (jobId: string) => {
    if (loadingViewJobId) {
      return;
    }

    try {
      setLoadingViewJobId(jobId);

      const fullJob = await dispatch(
        fetchJobById(jobId)
      ).unwrap();

      setViewingJob(fullJob);
    } catch (error) {
      console.error(
        "Failed to load job details:",
        error
      );
    } finally {
      setLoadingViewJobId(null);
    }
  };

  /* =========================================================
     CREATE JOB
  ========================================================= */

  const handleCreate = () => {
    setEditingJob(null);
    setShowDialog(true);
  };

  /* =========================================================
     DIALOG OPEN/CLOSE
  ========================================================= */

  const handleDialogOpenChange = (
    open: boolean
  ) => {
    setShowDialog(open);

    if (!open) {
      setEditingJob(null);
    }
  };

  /* =========================================================
     DELETE JOB
  ========================================================= */

  const handleDeleteJob = (
    jobId: string,
    jobTitle: string
  ) => {
    if (loadingDeleteJobId) {
      return;
    }

    setDeleteTarget({
      id: jobId,
      title: jobTitle,
    });

    setShowDeletePopup(true);
  };
  const handleConfirmDelete = async () => {
    if (!deleteTarget) {
      return;
    }

    const { id, title } = deleteTarget;

    setShowDeletePopup(false);

    try {
      setLoadingDeleteJobId(id);

      await dispatch(
        deleteJob(id)
      ).unwrap();

      setDeletedJobTitle(title);
      setShowDeleteSuccessPopup(true);
      setDeleteTarget(null);
    } catch (error) {
      console.error(
        "Failed to delete job:",
        error
      );
    } finally {
      setLoadingDeleteJobId(null);
    }
  };
  /* =========================================================
     RETURN
  ========================================================= */

  return (
    <div className="space-y-4 sm:space-y-6">

      {/* =================================================
          PAGE HEADER
      ================================================= */}

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight sm:text-2xl">
            Job Management
          </h1>

          <p className="text-muted-foreground">
            Create and manage job listings
          </p>
        </div>

        <Button
          type="button"
          onClick={handleCreate}
        >
          <Plus className="mr-2 h-4 w-4" />
          Create New Job
        </Button>
      </div>

      {/* =================================================
          SEARCH AND FILTER
      ================================================= */}

      <Card>
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">

            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />

              <Input
                placeholder="Search by title, department, or location..."
                className="pl-10"
                value={searchQuery}
                onChange={(event) =>
                  setSearchQuery(
                    event.target.value
                  )
                }
              />
            </div>

            <div className="w-full sm:w-auto">
              <Select
                value={filterStatus}
                onValueChange={setFilterStatus}
              >
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>

                <SelectContent>
                  <SelectItem value="all">
                    All Jobs
                  </SelectItem>

                  <SelectItem value="active">
                    Active
                  </SelectItem>

                  <SelectItem value="inactive">
                    Inactive
                  </SelectItem>

                  <SelectItem value="urgent">
                    Urgent
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

          </div>
        </CardContent>
      </Card>

      {/* =================================================
          ERROR
      ================================================= */}

      {error && (
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-destructive">
              {error}
            </p>
          </CardContent>
        </Card>
      )}

      {/* =================================================
          JOB TABLE
      ================================================= */}

      <Card>
        <CardContent className="p-0">
          <div className="max-h-[500px] overflow-y-auto overflow-x-auto">

            <Table>
              <TableHeader className="sticky top-0 z-20 bg-white">
                <TableRow>

                  <TableHead>
                    Job Title
                  </TableHead>

                  <TableHead>
                    Department
                  </TableHead>

                  <TableHead>
                    Location
                  </TableHead>

                  <TableHead>
                    Experience
                  </TableHead>

                  <TableHead>
                    Salary
                  </TableHead>

                  <TableHead>
                    Applications
                  </TableHead>

                  <TableHead>
                    Status
                  </TableHead>

                  <TableHead className="text-right">
                    Actions
                  </TableHead>

                </TableRow>
              </TableHeader>

              <TableBody>
                {/* =================================================
                  LOADING
              ================================================= */}



                {/* =================================================
                  EMPTY
              ================================================= */}

                {!loading && jobs.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      className="h-32 text-center text-muted-foreground"
                    >
                      No jobs found.
                    </TableCell>
                  </TableRow>
                )}

                {/* =================================================
                  JOBS
              ================================================= */}

                {!loading &&
                  jobs.map((job: any) => {

                    const isLoadingThisJob =
                      loadingEditJobId === job.id;

                    const isViewingThisJob =
                      loadingViewJobId === job.id;

                    const isDeletingThisJob =
                      loadingDeleteJobId === job.id;

                    return (
                      <TableRow key={job.id}>

                        {/* JOB TITLE */}

                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div>

                              <p className="font-medium">
                                {job.title}
                              </p>

                              <p className="text-xs text-muted-foreground">
                                {job.slug ||
                                  job.jobCode}
                              </p>

                            </div>
                          </div>
                        </TableCell>

                        {/* DEPARTMENT */}

                        <TableCell>
                          {job.department}
                        </TableCell>

                        {/* LOCATION */}

                        <TableCell>
                          <div className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />

                            {job.location}
                          </div>
                        </TableCell>

                        {/* EXPERIENCE */}

                        <TableCell>
                          <div className="flex items-center gap-1">
                            <GraduationCap className="h-3 w-3" />

                            {job.minimumExperience}
                            -
                            {job.maximumExperience ??
                              "-"}{" "}
                            yrs
                          </div>
                        </TableCell>

                        {/* SALARY */}

                        <TableCell>
                          <div className="flex items-center gap-1">
                            <span className="font-medium">
                              {formatSalaryRange(
                                job.minimumSalary,
                                job.maximumSalary,
                                job.currency
                              )}
                            </span>
                          </div>
                        </TableCell>

                        {/* APPLICATIONS */}

                        <TableCell>
                          <Badge
                            variant="secondary"
                            className="gap-1"
                          >
                            <Users className="h-3 w-3" />
                            {job.applicantCount ?? 0}
                          </Badge>
                        </TableCell>

                        {/* STATUS */}

                        <TableCell>
                          <div className="flex flex-col gap-1">

                            {job.priority ===
                              "HIGH" && (
                                <Badge
                                  variant="destructive"
                                  className="w-fit"
                                >
                                  Urgent
                                </Badge>
                              )}

                            {job.isActive ? (
                              <Badge
                                variant="default"
                                className="w-fit bg-green-600"
                              >
                                Active
                              </Badge>
                            ) : (
                              <Badge
                                variant="secondary"
                                className="w-fit"
                              >
                                Inactive
                              </Badge>
                            )}

                          </div>
                        </TableCell>

                        {/* ACTIONS */}

                        <TableCell className="text-right">

                          <div className="flex flex-wrap justify-end gap-2">

                            {/* VIEW */}

                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              disabled={
                                loadingViewJobId !==
                                null
                              }
                              onClick={() => {
                                void handleViewJob(
                                  job.id
                                );
                              }}
                            >
                              {isViewingThisJob ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                            </Button>

                            {/* EDIT */}

                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              disabled={
                                loadingEditJobId !==
                                null
                              }
                              onClick={() => {
                                void handleEditJob(
                                  job.id
                                );
                              }}
                            >
                              {isLoadingThisJob ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Pencil className="h-4 w-4" />
                              )}
                            </Button>

                            {/* DELETE */}

                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="text-destructive hover:text-destructive"
                              disabled={
                                loadingDeleteJobId !==
                                null
                              }
                              onClick={() => {
                                void handleDeleteJob(
                                  job.id,
                                  job.title
                                );
                              }}
                            >
                              {isDeletingThisJob ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </Button>

                          </div>

                        </TableCell>

                      </TableRow>
                    );
                  })}

              </TableBody>

            </Table>
          </div>

        </CardContent>
      </Card>

      {/* =================================================
          CREATE / EDIT JOB DIALOG
      ================================================= */}

      <CreateJobDialog
        open={showDialog}
        onOpenChange={
          handleDialogOpenChange
        }
        editingJob={editingJob}
      />

      {/* =================================================
          VIEW JOB DIALOG
      ================================================= */}

      <Dialog
        open={viewingJob !== null}
        onOpenChange={(open) => {
          if (!open) {
            setViewingJob(null);
          }
        }}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">

          <DialogHeader>
            <DialogTitle>
              {viewingJob?.title || "Job Details"}
            </DialogTitle>
          </DialogHeader>

          {viewingJob && (
            <div className="space-y-6">

              {/* BASIC INFORMATION */}

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">

                <div>
                  <p className="text-sm text-muted-foreground">
                    Job Code
                  </p>

                  <p className="font-medium">
                    {viewingJob.jobCode}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-muted-foreground">
                    Department
                  </p>

                  <p className="font-medium">
                    {viewingJob.department}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-muted-foreground">
                    Location
                  </p>

                  <p className="font-medium">
                    {viewingJob.location}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-muted-foreground">
                    Employment Type
                  </p>

                  <p className="font-medium">
                    {viewingJob.employmentType}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-muted-foreground">
                    Workplace Type
                  </p>

                  <p className="font-medium">
                    {viewingJob.workplaceType}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-muted-foreground">
                    Experience
                  </p>

                  <p className="font-medium">
                    {viewingJob.minimumExperience}
                    -
                    {viewingJob.maximumExperience ??
                      "-"}{" "}
                    years
                  </p>
                </div>

                <div>
                  <p className="text-sm text-muted-foreground">
                    Salary
                  </p>

                  <p className="font-medium">
                    {formatSalaryRange(
                      viewingJob.minimumSalary,
                      viewingJob.maximumSalary,
                      viewingJob.currency
                    )}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-muted-foreground">
                    Positions
                  </p>

                  <p className="font-medium">
                    {viewingJob.positions}
                  </p>
                </div>

              </div>

              {/* STATUS */}

              <div className="flex flex-wrap gap-2">

                {viewingJob.priority ===
                  "HIGH" && (
                    <Badge variant="destructive">
                      Urgent
                    </Badge>
                  )}

                <Badge>
                  {viewingJob.status}
                </Badge>

                <Badge variant="secondary">
                  {viewingJob.isActive
                    ? "Active"
                    : "Inactive"}
                </Badge>

              </div>

              {/* ABOUT ROLE */}

              {viewingJob.aboutRole && (
                <div>
                  <h3 className="mb-2 font-semibold">
                    About the Role
                  </h3>

                  <p className="whitespace-pre-wrap text-sm text-muted-foreground">
                    {viewingJob.aboutRole}
                  </p>
                </div>
              )}

              {/* DESCRIPTION */}

              <div>
                <h3 className="mb-2 font-semibold">
                  Description
                </h3>

                <p className="whitespace-pre-wrap text-sm text-muted-foreground">
                  {viewingJob.description}
                </p>
              </div>

              {/* RESPONSIBILITIES */}

              {viewingJob.responsibilities
                ?.length > 0 && (
                  <div>
                    <h3 className="mb-2 font-semibold">
                      Responsibilities
                    </h3>

                    <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                      {viewingJob.responsibilities.map(
                        (item, index) => (
                          <li key={index}>
                            {item}
                          </li>
                        )
                      )}
                    </ul>
                  </div>
                )}

              {/* REQUIREMENTS */}

              {viewingJob.requirements
                ?.length > 0 && (
                  <div>
                    <h3 className="mb-2 font-semibold">
                      Requirements
                    </h3>

                    <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                      {viewingJob.requirements.map(
                        (item, index) => (
                          <li key={index}>
                            {item}
                          </li>
                        )
                      )}
                    </ul>
                  </div>
                )}

              {/* SKILLS */}

              {viewingJob.skills?.length > 0 && (
                <div>
                  <h3 className="mb-2 font-semibold">
                    Skills
                  </h3>

                  <div className="flex flex-wrap gap-2">
                    {viewingJob.skills.map(
                      (skill, index) => (
                        <Badge
                          key={index}
                          variant="secondary"
                        >
                          {skill}
                        </Badge>
                      )
                    )}
                  </div>
                </div>
              )}

              {/* BENEFITS */}

              {viewingJob.benefits?.length > 0 && (
                <div>
                  <h3 className="mb-2 font-semibold">
                    Benefits
                  </h3>

                  <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                    {viewingJob.benefits.map(
                      (benefit, index) => (
                        <li key={index}>
                          {benefit}
                        </li>
                      )
                    )}
                  </ul>
                </div>
              )}

            </div>
          )}

        </DialogContent>
      </Dialog>
      <AlreadyAppliedDialog
        open={showDeletePopup}
        type="delete"
        title="Delete Job?"
        message={
          deleteTarget
            ? `Are you sure you want to delete "${deleteTarget.title}"? This action cannot be undone.`
            : "Are you sure you want to delete this job?"
        }
        buttonText="Delete"
        secondaryButtonText="Cancel"
        onOpenChange={(open) => {
          setShowDeletePopup(open);

          if (!open) {
            setDeleteTarget(null);
          }
        }}
        onSecondaryAction={() => {
          setShowDeletePopup(false);
          setDeleteTarget(null);
        }}
        onViewApplicationStatus={handleConfirmDelete}
      />
      <AlreadyAppliedDialog
        open={showDeleteSuccessPopup}
        type="deleteSuccess"
        title="Job Deleted Successfully"
        message={`${deletedJobTitle} has been deleted successfully.`}
        buttonText="Done"
        onOpenChange={(open) => {
          setShowDeleteSuccessPopup(open);

          if (!open) {
            setDeletedJobTitle("");
          }
        }}
        onViewApplicationStatus={() => {
          setShowDeleteSuccessPopup(false);
          setDeletedJobTitle("");
        }}
      />

    </div>
  );
}